<template>
    <div v-if="aam.mapLoading" class="as-applied-legend">
        <ProgressSpinner
            style="width: 20px; height: 20px"
            strokeWidth="4"
            fill="#212121"
            animationDuration=".5s"
            aria-label="Loading as-applied map"
        />
        <span class="as-applied-legend-loading-text">{{ $t('Loading as-applied map...') }}</span>
    </div>
    <div v-else-if="aam.visible" class="as-applied-legend">
        <div class="as-applied-legend-header">
            <strong>{{ $t('As-applied map') }}</strong>
            <Button
                icon="pi pi-times"
                severity="danger"
                variant="text"
                size="small"
                @click="aam.clearMap(true)"
                v-tooltip.bottom="$t('Clear map')"
            />
        </div>
        <div v-if="aam.activeFilter" class="as-applied-legend-title">
            <span v-if="activeFilterLabel">{{ activeFilterLabel }}</span>
            <span v-else-if="aam.isWorkingSpeed">
                {{ $t('Working speed') }} (km/h)
            </span>
            <span v-else>
                {{ aam.activeFilter.nexatMachineId }} · {{ $t('Machine') }}: {{ aam.activeFilter.machineId }} · {{ $t('Boom') }}: {{ aam.activeFilter.boomId }}
            </span>
        </div>
        <div v-if="hasRateLegend" class="as-applied-legend-rate">
            <span class="as-applied-legend-rate-label">{{ $t('Rate') }}:</span>
            <div class="as-applied-legend-rate-bar" :style="{ background: rateLegendGradient }">
                <span class="as-applied-legend-rate-value as-applied-legend-rate-value--min">
                    {{ formattedMinRate }}
                </span>
                <span class="as-applied-legend-rate-value as-applied-legend-rate-value--max">
                    {{ formattedMaxRate }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { createDraggable } from 'animejs'
import { useAsAppliedMap } from '@/stores/asAppliedMap'
import { RATE_LEGEND_GRADIENT, toDisplayRate } from '@/helpers/asAppliedMapRate'
import { useRoute } from 'vue-router'

export default {
    name: 'AsAppliedMapLegend',
    setup() {
        const route = useRoute()
        const aam = useAsAppliedMap()

        return { route, aam }
    },
    computed: {
        rateLegendGradient: () => RATE_LEGEND_GRADIENT,
        hasRateLegend() {
            return Number.isFinite(this.aam.featureCollection?.min_rate)
                && Number.isFinite(this.aam.featureCollection?.max_rate)
        },
        formattedMinRate() {
            return this.formatRateValue(this.aam.featureCollection?.min_rate)
        },
        formattedMaxRate() {
            return this.formatRateValue(this.aam.featureCollection?.max_rate)
        },
        isLegendContentVisible() {
            return this.aam.visible && !this.aam.mapLoading
        },
        activeFilterLabel() {
            const name = this.aam.activeFilter?.boomName
            if (!name || name === '-') {
                return null
            }
            return name
        },
    },
    methods: {
        formatRateValue(value) {
            const display = toDisplayRate(value, this.aam.isWorkingSpeed)
            if (display === null) {
                return '-'
            }
            return Math.round(display).toLocaleString('en-US')
        },
    },
    watch: {
        'route.name': {
            handler(to, from) {
                if (this.aam.visible && to !== from) {
                    this.aam.clearMap(true)
                    this.aam.clearMachinesAndBooms()
                }
            },
        },
        isLegendContentVisible: {
            immediate: true,
            handler(visible) {
                if (visible) {
                    if (!this.aam.legendDraggable) {
                        this.aam.legendDraggable = createDraggable('.as-applied-map-legend-control', {
                            container: '#map',
                            snap: 56,
                            dragThreshold: { mouse: 6, touch: 10 },
                        })
                    } else {
                        this.aam.legendDraggable.enable()
                        this.aam.legendDraggable.refresh()
                    }
                } else if (this.aam.legendDraggable) {
                    this.aam.legendDraggable.disable()
                }
            }
        },
    }
}
</script>

<style scoped>
.as-applied-legend {
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: #18212a;
    color: #fff;
    min-width: 120px;
    padding: 8px;
    border-radius: 8px;
    border: 1px solid #3e4953;
}

.as-applied-legend-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.as-applied-legend-title {
    font-size: 12px;
    font-weight: 700;
}

.as-applied-legend-rate {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.as-applied-legend-rate-label {
    font-size: 11px;
    color: #c9c9c9;
}

.as-applied-legend-rate-bar {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 180px;
    height: 22px;
    border-radius: 2px;
    overflow: hidden;
}

.as-applied-legend-rate-value {
    position: relative;
    z-index: 1;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    padding: 0 4px;
}

.as-applied-legend-rate-value--min {
    color: #fff;
}

.as-applied-legend-rate-value--max {
    color: #fff;
}

.as-applied-legend-loading-text {
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    color: #ebebeb;
}
</style>
